import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Users, Star, Headphones, Heart, Zap, ArrowRight, Play } from 'lucide-react';
import api from '../services/api';
import LandingPageMeta from '../components/meta/LandingPageMeta';
import SongsStream from '../components/SongsStream';
import VideosStream from '../components/VideosStream';
import ImagesStream from '../components/ImagesStream';
import UpcomingEvents from '../components/UpcomingEvents';
import FeaturedSongs from '../components/FeaturedSongs';
import RecentShows from '../components/RecentShows';
import LatestDeepWaveSongs from '../components/LatestDeepWaveSongs';
import LatestDeepWaveVideos from '../components/LatestDeepWaveVideos';
import LatestDeepWaveImages from '../components/LatestDeepWaveImages';
import TopDeepWaveTracks from '../components/TopDeepWaveTracks';
import TopDeepWaveImages from '../components/TopDeepWaveImages';
import HighlightedArtists from '../components/HighlightedArtists';
import DeepWaveCrew from '../components/DeepWaveCrew';
import BandMessage from '../components/BandMessage';

const LandingPage = () => {
    const [isVisible, setIsVisible] = useState(true);

    const features = [
        {
            icon: <Users className="w-8 h-8" />,
            title: "Connect with Artists",
            description: "Join our vibrant community of musicians, visual artists, performers, and creative collaborators.",
            delay: "delay-100"
        },
        {
            icon: <Headphones className="w-8 h-8" />,
            title: "Collaborative Creation",
            description: "Work together on projects, share resources, and create artistic experiences.",
            delay: "delay-200"
        },
        {
            icon: <Star className="w-8 h-8" />,
            title: "Ensemble Showcase",
            description: "Feature your work in ensemble exhibitions, performances, and artistic collaborations.",
            delay: "delay-300"
        }
    ];

    return (
        <div className="min-h-screen bg-hero w-full">
            <LandingPageMeta />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 w-full">
                <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
                    <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-48 sm:w-72 h-48 sm:h-72 bg-blue-600/10 rounded-full blur-3xl animate-pulse-slow" style={{ willChange: 'opacity' }}></div>
                    <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-64 sm:w-96 h-64 sm:h-96 bg-blue-800/10 rounded-full blur-3xl animate-pulse-slow delay-200" style={{ willChange: 'opacity' }}></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 sm:w-[400px] lg:w-[600px] h-72 sm:h-[400px] lg:h-[600px] bg-gradient-radial from-blue-600/5 to-transparent rounded-full animate-float" style={{ willChange: 'transform' }}></div>
                </div>

                <div className="relative z-10 max-w-7xl mx-auto w-full px-2 sm:px-0">
                    <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center w-full min-w-0">
                        <div className="space-y-6 sm:space-y-8 w-full min-w-0">
                            <div className="text-center lg:text-left">
                                <div className={`mb-4 sm:mb-6 ${isVisible ? 'animate-fade-in delay-200' : 'opacity-0'}`}>
                                    <p className="text-sm sm:text-base font-medium text-gray-400 mb-1 mt-6 tracking-widest">
                                        Welcome to
                                    </p>
                                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black gradient-text leading-tight">
                                        The Deep Wave
                                    </h1>
                                    <p className="text-sm sm:text-base md:text-lg font-bold text-gray-300 mb-2 sm:mb-3 text-shadow tracking-wide">
                                        Arts & Sound Ensemble
                                    </p>
                                </div>

                                <div className={`mb-6 sm:mb-8 w-full ${isVisible ? 'animate-fade-in delay-300' : 'opacity-0'}`}>
                                    <div className="glass-card -mx-6 lg:mx-0 rounded-none lg:rounded-2xl p-3 sm:p-4 border-y lg:border border-yellow-500/30 min-w-0">
                                        <div className="w-full min-w-0">
                                            <UpcomingEvents limit={4} />
                                        </div>
                                    </div>
                                </div>

                                <div className={`mb-6 sm:mb-8 ${isVisible ? 'animate-fade-in delay-400' : 'opacity-0'}`}>
                                    <p className="text-base sm:text-xl text-gray-400 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                                        We're a South Florida-based music ensemble blending psychedelic jam rock, reggae, and funk into immersive shows featuring live art, performance, and aerial acts. 10% of website proceeds support beach cleanup efforts, connecting art with impact.
                                    </p>
                                </div>

                                <div className={`flex justify-center lg:justify-start items-center ${isVisible ? 'animate-fade-in delay-500' : 'opacity-0'}`}>
                                    <Link
                                        to="/register"
                                        className="btn-primary flex items-center gap-2 sm:gap-3 text-base sm:text-lg group px-4 sm:px-6 py-3 w-full sm:w-auto justify-center sm:justify-start"
                                    >
                                        <Users className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-300" />
                                        <span className="whitespace-nowrap">Create a Profile</span>
                                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div className={`${isVisible ? 'animate-fade-in delay-600' : 'opacity-0'} mt-6 lg:mt-0 w-full min-w-0`}>
                            <div className="glass-card -mx-6 lg:mx-0 rounded-none lg:rounded-2xl p-4 sm:p-6 border-y lg:border border-yellow-500/30 min-w-0">
                                <div className="w-full min-w-0">
                                    <FeaturedSongs limit={2} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-transparent to-gray-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-10 sm:mb-16">
                        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-4 sm:mb-6">
                            Get Involved with<br />
                            The Deep Wave
                        </h2>
                        <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                            Create an account and login to join The Deep Wave's community where creativity flourishes. Connect with artists across all disciplines and be part of a community that celebrates artistic expression in all its forms.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 sm:gap-8 mb-10 sm:mb-16">
                        {features.map((feature, index) => (
                            <div key={index} className={`feature-card hover-lift hover-glow text-center ${feature.delay}`}>
                                <div className="text-blue-400 mb-4 sm:mb-6 flex justify-center">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-400 text-sm sm:text-base">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>

                    <div className="glass-card p-6 sm:p-12 text-center">
                        <div className="flex items-center justify-center mb-5 sm:mb-8">
                            <Zap className="w-10 h-10 sm:w-12 sm:h-12 text-yellow-400" />
                        </div>
                        <h3 className="text-2xl sm:text-4xl font-black gradient-text mb-4 sm:mb-6">
                            Live Music & Arts Experience
                        </h3>
                        <p className="text-base sm:text-xl text-gray-400 mb-6 sm:mb-8 max-w-3xl mx-auto">
                            Come to shows and events to participate in live creative sessions and join real-time collaboration with The Deep Wave's mix of musicians and artists of all kinds.
                        </p>
                    </div>
                </div>
            </section>

            {/* Deep Wave Crew Section */}
            <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-b from-gray-900/30 to-gray-900/50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-10 sm:mb-16">
                        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-4 sm:mb-6">
                            The Deep Wave Crew
                        </h2>
                        <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                            Meet the passionate team behind The Deep Wave's music. Our crew members are dedicated artists,
                            curators, and creative professionals who worked together to create the art that you are seeing throughout this website.
                        </p>
                    </div>

                    <DeepWaveCrew showTitle={false} className="border border-blue-600/30" />
                </div>
            </section>

            {/* Top Tracks + Top Artwork */}
            <div className="pt-12 sm:pt-24 px-4 sm:px-6 sm:max-w-7xl sm:mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-blue-500/30">
                        <TopDeepWaveTracks limit={5} viewAllPosition="bottom" />
                    </div>
                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-cyan-500/30">
                        <TopDeepWaveImages limit={10} viewAllPosition="bottom" />
                    </div>
                </div>
            </div>

            {/* Message from the Band */}
            <BandMessage />

            {/* Official Deep Wave Content Section */}
            <section className="py-12 sm:py-24 bg-gradient-to-b from-gray-900/40 to-gray-900/60">
                {/* Section header — padded */}
                <div className="px-4 sm:px-6 max-w-7xl mx-auto">
                    <div className="text-center mb-8 sm:mb-16">
                        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-4 sm:mb-6">
                            The Deep Wave<br />Official Content
                        </h2>
                        <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                            Here are the latest official works from The Deep Wave arts and sound ensemble.
                            You can find exclusive artistic content, behind-the-scenes material, and the collaborative works of art that define The Deep Wave.
                        </p>
                    </div>
                </div>

                {/* Media cards — edge-to-edge on mobile, constrained on desktop */}
                <div className="space-y-4 sm:space-y-12 sm:max-w-7xl sm:mx-auto sm:px-6">
                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-blue-600/30">
                        <LatestDeepWaveSongs limit={4} viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-blue-800/30">
                        <LatestDeepWaveVideos limit={4} viewAllPosition="bottom" />
                    </div>

                    {/* Featured Artists */}
                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-yellow-500/30">
                        <div className="text-center mb-8 sm:mb-16 px-1 sm:px-0">
                            <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-4 sm:mb-6">
                                Featured Artists
                            </h2>
                            <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                                Here are the remarkable artists currently highlighted in The Deep Wave ensemble.
                                These featured creators are pushing boundaries, exploring new artistic territories, and inspiring our community.
                            </p>
                        </div>
                        <HighlightedArtists limit={6} showTitle={false} />
                    </div>

                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-cyan-500/30">
                        <LatestDeepWaveImages limit={6} viewAllPosition="bottom" />
                    </div>
                </div>
            </section>

            {/* Community Content Section */}
            <section className="py-12 sm:py-24 bg-gradient-to-b from-gray-900/60 to-transparent">
                {/* Section header — padded */}
                <div className="px-4 sm:px-6 max-w-7xl mx-auto">
                    <div className="text-center mb-8 sm:mb-16">
                        <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-4 sm:mb-6">
                            Community Creations
                        </h2>
                        <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                            Here are the latest creative works shared by The Deep Wave community members and guest artists.
                            Join to experience the communities artistic content and connect with other talented creators.
                        </p>
                    </div>
                </div>

                {/* Media cards — edge-to-edge on mobile */}
                <div className="space-y-4 sm:space-y-12 sm:max-w-7xl sm:mx-auto sm:px-6">
                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-blue-500/30">
                        <SongsStream limit={4} viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-green-500/30">
                        <VideosStream limit={4} viewAllPosition="bottom" />
                    </div>

                    <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-cyan-500/30">
                        <ImagesStream limit={6} viewAllPosition="bottom" />
                    </div>
                </div>

                {/* Join CTA */}
                <div className="text-center mt-10 sm:mt-16 px-4 sm:px-6">
                    <div className="glass-card p-6 sm:p-8 max-w-2xl mx-auto border border-blue-600/30">
                        <div className="text-4xl mb-4">🎵</div>
                        <h3 className="text-xl sm:text-2xl font-bold gradient-text mb-4">
                            Want to share your art?
                        </h3>
                        <p className="text-gray-400 mb-6 text-sm sm:text-base">
                            Join The Deep Wave community to share your creative works,
                            connect with fellow artists, and become part of our artistic community.
                        </p>
                        <Link to="/register" className="btn-primary inline-flex items-center justify-center gap-2 mx-auto">
                            <Music className="w-5 h-5" />
                            Create An Account
                        </Link>
                    </div>
                </div>
            </section>

            {/* Events Section */}
            <section className="py-12 sm:py-24 bg-gradient-to-b from-gray-900/30 to-gray-900/50">
                {/* Section header — padded */}
                <div className="px-4 sm:px-6 max-w-7xl mx-auto">
                    <div className="text-center mb-8 sm:mb-16">
                        <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black gradient-text mb-4 sm:mb-6">
                            The Deep Wave Events
                        </h2>
                        <p className="text-base sm:text-xl text-gray-400 max-w-3xl mx-auto">
                            Experience artistic events, performances, and collaborative showcases happening with The Deep Wave.
                            From gallery openings to collaborative performances, enjoy the full spectrum of The Deep Wave's artistic expression.
                        </p>
                    </div>
                </div>

                {/* Events grid — edge-to-edge on mobile */}
                <div className="sm:max-w-7xl sm:mx-auto sm:px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
                        <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-green-500/30">
                            <RecentShows limit={4} />
                        </div>
                        <div className="glass-card rounded-none sm:rounded-2xl p-3 sm:p-8 border-y sm:border border-yellow-500/30">
                            <UpcomingEvents limit={4} />
                        </div>
                    </div>
                </div>

                {/* Events CTA */}
                <div className="text-center mt-10 sm:mt-16 px-4 sm:px-6">
                    <div className="glass-card p-6 sm:p-8 max-w-2xl mx-auto border border-blue-600/30">
                        <div className="text-4xl mb-4">🎪</div>
                        <h3 className="text-xl sm:text-2xl font-bold gradient-text mb-4">
                            Want to organize an ensemble event?
                        </h3>
                        <p className="text-gray-400 mb-6 text-sm sm:text-base">
                            Create and manage artistic events with The Deep Wave.
                            Connect with fellow artists and build an event through collaborations.
                        </p>
                        <Link to="/register" className="btn-primary inline-flex items-center justify-center gap-2 w-full sm:w-auto">
                            <Play className="w-5 h-5 text-white" />
                            <span>Organize An Event</span>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-12 sm:py-24 px-4 sm:px-6 bg-gradient-to-r from-blue-900/20 via-gray-900/20 to-blue-900/20">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl sm:text-5xl md:text-6xl font-black gradient-text mb-6 sm:mb-8">
                        Join The Deep Wave Community!
                    </h2>
                    <p className="text-base sm:text-xl text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto">
                        Be part of the artistic revolution. Whether you're a creator looking to share your work,
                        an art enthusiast seeking collaborative experiences, or a fan of the music, we're here for your enjoyment.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center">
                        <Link
                            to="/register"
                            className="btn-primary flex items-center gap-3 text-lg sm:text-xl group px-8 sm:px-12 py-4 sm:py-5 w-full sm:w-auto justify-center"
                        >
                            <Play className="w-6 h-6" />
                            Sign Up Now
                            <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                        <Link
                            to="/login"
                            className="btn-ghost flex items-center gap-3 text-base sm:text-xl px-6 sm:px-8 py-3 text-center"
                        >
                            Already have an account? Sign in
                        </Link>
                    </div>
                </div>
            </section>

        </div>
    );
};

export default LandingPage;
